/** Answering-machine-detection verdicts for the calling rep's own live call.
 *
 * Module-scoped so `useDialerSession` (itself a per-tab singleton) can read the
 * verdict directly while the page owns the connection lifecycle.
 *
 * Unlike `useDialerStream`, an `amd_verdict` is a one-shot fact about a call that
 * is happening right now — there is nothing to refetch and nothing to reconcile,
 * so there is no debounce, no snapshot, and no safety refetch. If the stream is
 * down the verdict is simply missed and the call behaves as it did before AMD
 * existed: the rep hears the machine and hangs up.
 */
import { readonly, ref } from 'vue'
import { apiFetch } from '../services/api'
import { runSseStream } from '../services/sseStream'

export interface AmdVerdict {
  dispatch_id: string
  answered_by: string
}

const lastVerdict = ref<AmdVerdict | null>(null)
const connectionState = ref<'connecting' | 'open' | 'closed'>('closed')

let abortController: AbortController | null = null
let stopped = true
let reconnectAttempts = 0
let reconnectTimer: ReturnType<typeof setTimeout> | null = null

async function _run(): Promise<void> {
  if (stopped) return
  connectionState.value = 'connecting'
  abortController = new AbortController()
  try {
    const response = await apiFetch('/api/dialer/session/stream', {
      method: 'GET',
      responseType: 'raw',
      signal: abortController.signal,
      headers: { Accept: 'text/event-stream' },
    })
    connectionState.value = 'open'
    reconnectAttempts = 0
    for await (const evt of runSseStream(response)) {
      if (stopped) break
      if (evt.type !== 'amd_verdict') continue
      const { dispatch_id, answered_by } = evt.payload || {}
      if (dispatch_id && answered_by) lastVerdict.value = { dispatch_id, answered_by }
    }
  } catch (err: any) {
    if (err?.name === 'AbortError') return
  } finally {
    connectionState.value = 'closed'
    abortController = null
    if (!stopped) _scheduleReconnect()
  }
}

function _scheduleReconnect(): void {
  if (stopped || reconnectTimer !== null) return
  reconnectAttempts += 1
  const delay = Math.min(30000, 500 * 2 ** Math.min(reconnectAttempts, 6))
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    void _run()
  }, delay)
}

export function openAmdStream(): void {
  if (!stopped) return
  stopped = false
  reconnectAttempts = 0
  void _run()
}

export function closeAmdStream(): void {
  stopped = true
  abortController?.abort()
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  lastVerdict.value = null
}

export function useDialerAmdStream() {
  return {
    lastVerdict: readonly(lastVerdict),
    connectionState: readonly(connectionState),
    open: openAmdStream,
    close: closeAmdStream,
  }
}
